<template>
  <a-modal
    v-if="visible"
    title="Create Final Paycheck"
    :visible="visible"
    :width="920"
    :footer="null"
    :mask-closable="false"
    :destroy-on-close="true"
    wrap-class-name="final-paycheck-modal"
    data-testid="final-paycheck-modal"
    @cancel="closeModal"
  >
    <div v-if="loading" class="final-paycheck-loading">
      <a-spin size="large" />
    </div>

    <a-result v-else-if="loadError" status="error" title="Unable to load final paycheck" :sub-title="loadError">
      <a-button slot="extra" type="primary" @click="loadPaycheck">Retry</a-button>
    </a-result>

    <template v-else-if="isEmptyState">
      <a-result
        status="info"
        title="No unpaid shifts found"
        sub-title="There are no shifts to include in this final paycheck. Confirm that no additional payment is needed in ePeople or that any remaining payment will be handled outside ePeople."
      >
        <div slot="extra" class="final-paycheck-result-actions">
          <a-button @click="closeModal">Continue Reviewing</a-button>
          <a-button type="primary" @click="confirmCompleteWithoutPayment">Complete Final Paycheck</a-button>
        </div>
      </a-result>
      <a-alert
        v-if="submitError"
        type="error"
        show-icon
        message="Final paycheck was not completed"
        :description="submitError"
      />
    </template>

    <a-result
      v-else-if="!isActionable"
      status="info"
      title="Final paycheck is not available"
      :sub-title="inactiveStateMessage"
    >
      <a-button slot="extra" @click="closeModal">Close</a-button>
    </a-result>

    <template v-else>
      <a-descriptions class="final-paycheck-summary" :column="2" size="small">
        <a-descriptions-item label="Employee">
          {{ paycheck.employee.name }}
        </a-descriptions-item>
        <a-descriptions-item label="Associate Number">
          {{ paycheck.employee.associate_number }}
        </a-descriptions-item>
        <a-descriptions-item label="Final Paycheck Due">
          {{ formatDateTime(paycheck.final_payment_due_at) }}
        </a-descriptions-item>
        <a-descriptions-item label="Total">
          <strong>{{ formatMoney(selectedTotal) }}</strong>
        </a-descriptions-item>
      </a-descriptions>

      <a-alert
        class="final-paycheck-info"
        type="info"
        show-icon
        message="Select the shifts to include in this final paycheck."
      />

      <final-paycheck-shifts-table
        :shifts="paycheck.shifts"
        :selected-shift-ids="selectedShiftIds"
        @update:selectedShiftIds="setSelectedShiftIds"
      />

      <a-alert
        v-if="submitError"
        class="final-paycheck-error"
        type="error"
        show-icon
        message="Final paycheck was not completed"
        description="No duplicate payment was created. Review the selected shifts and try again."
      />
      <p v-if="submitError" class="final-paycheck-error-details">{{ submitError }}</p>

      <div class="final-paycheck-footer">
        <a-button :disabled="submitting" @click="closeModal">Cancel</a-button>
        <a-button type="primary" :disabled="!shiftIds.length" :loading="submitting" @click="createPaycheck">
          {{ submitError ? 'Retry Final Paycheck' : 'Create Final Paycheck' }}
        </a-button>
      </div>
    </template>
  </a-modal>
</template>

<script>
import { Alert, Button, Descriptions, Modal, notification, Result, Spin } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import util from '@/util'
import FinalPaycheckShiftsTable from './final-paycheck-shifts-table.vue'

const { mapActions: paymentActions } = createNamespacedHelpers('payment')
const { mapActions: profileActions } = createNamespacedHelpers('profile')

const ACTIONABLE_STATES = ['PREPARING', 'OVERDUE']

export default {
  name: 'FinalPaycheckModal',
  components: {
    'a-alert': Alert,
    'a-button': Button,
    'a-descriptions': Descriptions,
    'a-descriptions-item': Descriptions.Item,
    'a-modal': Modal,
    'a-result': Result,
    'a-spin': Spin,
    'final-paycheck-shifts-table': FinalPaycheckShiftsTable
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    processId: {
      type: [Number, String],
      required: true
    }
  },
  data() {
    return {
      loading: false,
      submitting: false,
      paycheck: null,
      selectedShiftIds: [],
      loadError: '',
      submitError: ''
    }
  },
  computed: {
    isEmptyState() {
      return this.paycheck?.state === 'NO_UNPAID_SHIFTS'
    },

    isActionable() {
      return ACTIONABLE_STATES.includes(this.paycheck?.state) && this.paycheck.shifts?.length > 0
    },

    inactiveStateMessage() {
      return this.paycheck?.state
        ? `The final paycheck process is in the ${this.paycheck.state} state and has no available action.`
        : 'The final paycheck process is no longer active.'
    },

    shiftIds() {
      return this.selectedShiftIds
    },

    selectedShifts() {
      const selectedShiftIds = new Set(this.shiftIds)

      return (this.paycheck?.shifts ?? []).filter(shift => selectedShiftIds.has(shift.id))
    },

    selectedTotal() {
      if (this.selectedShifts.some(shift => shift.amount == null)) {
        return null
      }

      return this.selectedShifts.reduce((total, shift) => total + shift.amount, 0)
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler(value) {
        if (value) {
          this.loadPaycheck()
        }
      }
    }
  },
  methods: {
    ...paymentActions(['getFinalPaycheck', 'createFinalPaycheck', 'completeFinalPaycheck']),
    ...profileActions(['getAssociateById']),

    formatDateTime(value) {
      return util.convertDateTimeToCurrentTimeZoneFormattedString(value) ?? '—'
    },

    formatMoney(value) {
      return value == null ? '—' : `$${util.convertDbToMoney(value).toFixed(2)}`
    },

    getErrorMessage(error) {
      return typeof error === 'string' ? error : (error?.message ?? 'An unexpected error occurred.')
    },

    async loadPaycheck() {
      this.loading = true
      this.loadError = ''
      this.submitError = ''
      this.paycheck = null

      try {
        this.paycheck = await this.getFinalPaycheck({ process_id: this.processId })
        this.setSelectedShiftIds(this.paycheck.initial_shift_ids ?? this.paycheck.shifts.map(shift => shift.id))
      } catch (error) {
        this.loadError = this.getErrorMessage(error)
      } finally {
        this.loading = false
      }
    },

    async createPaycheck() {
      if (!this.shiftIds.length) {
        return
      }

      this.submitting = true
      this.submitError = ''

      try {
        const result = await this.createFinalPaycheck({
          process_id: this.processId,
          shift_ids: this.shiftIds
        })

        await this.finishFlow(result, 'Payment(s) successfully recorded!')
      } catch (error) {
        this.submitError = this.getErrorMessage(error)
      } finally {
        this.submitting = false
      }
    },

    setSelectedShiftIds(selectedShiftIds) {
      const availableShiftIds = new Set((this.paycheck?.shifts ?? []).map(shift => shift.id))
      this.selectedShiftIds = [...new Set(selectedShiftIds)].filter(shiftId => availableShiftIds.has(shiftId))
    },

    confirmCompleteWithoutPayment() {
      Modal.confirm({
        title: 'Complete final paycheck without shifts?',
        content: `This will complete the final paycheck process and disable ${this.paycheck.employee.name}’s profile. Confirm that no additional payment is needed in ePeople or that any remaining payment will be handled outside ePeople.`,
        okText: 'Complete Final Paycheck',
        cancelText: 'Continue Reviewing',
        onOk: () => this.completeWithoutPayment()
      })
    },

    async completeWithoutPayment() {
      this.submitError = ''

      try {
        const result = await this.completeFinalPaycheck({ process_id: this.processId })
        await this.finishFlow(result, 'Final paycheck completed successfully.')
      } catch (error) {
        this.submitError = this.getErrorMessage(error)
      }
    },

    async finishFlow(result, successDescription) {
      try {
        await this.getAssociateById({ associate_id: this.paycheck.employee.id })
      } catch (error) {
        notification.warning({
          message: 'Profile refresh failed',
          description: this.getErrorMessage(error)
        })
      }

      notification.success({
        message: 'Success',
        description: successDescription
      })

      this.$emit('completed', result)
      this.$emit('update:visible', false)
    },

    closeModal() {
      if (!this.submitting) {
        this.$emit('update:visible', false)
      }
    }
  }
}
</script>

<style scoped>
.final-paycheck-loading {
  display: flex;
  min-height: 280px;
  align-items: center;
  justify-content: center;
}

.final-paycheck-summary,
.final-paycheck-info,
.final-paycheck-error {
  margin-bottom: 16px;
}

.final-paycheck-error {
  margin-top: 16px;
}

.final-paycheck-error-details {
  margin: -8px 0 16px;
  color: var(--error-color);
}

.final-paycheck-footer,
.final-paycheck-result-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.final-paycheck-footer {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid var(--border-color-split);
}
</style>
